// Countries list with phone dial codes
// Sorted alphabetically by country name
// Note: Some countries share dial codes (e.g., +1 for NANP countries, +7 for Russia/Kazakhstan)

/**
 * Utility function to convert phone placeholder format (XXX XXXX) to regex pattern
 * @param placeholder - Phone placeholder format with X representing digits
 * @returns Regex pattern string for validation
 */
export function placeholderToPattern(placeholder: string): string {
  // Replace X with \d for digit matching
  // Keep spaces as optional (\s?)
  return placeholder.replace(/X/g, "\\d").replace(/\s/g, "\\s?");
}

/**
 * Get phone pattern from country by dial code
 * @param dialCode - Country dial code (e.g., "+1")
 * @returns Phone pattern regex string
 */
export function getPhonePatternByDialCode(dialCode: string): string {
  const country = countries.find((c) => c.dial_code === dialCode);
  return country ? country.phone_pattern : "\\d{7,15}"; // Default pattern
}

/**
 * Validate if a phone number starts with valid mobile prefix for the country
 * @param phoneNumber - Phone number without country code
 * @param dialCode - Country dial code (e.g., "+20")
 * @returns Boolean indicating if the number has a valid mobile prefix
 */
export function isValidMobilePrefix(
  phoneNumber: string,
  dialCode: string
): boolean {
  const country = countries.find((c) => c.dial_code === dialCode);
  if (
    !country ||
    !country.mobile_prefixes ||
    country.mobile_prefixes.length === 0
  ) {
    return true; // If no prefixes defined, allow all numbers
  }

  // Remove spaces and non-digit characters from phone number
  const cleanNumber = phoneNumber.replace(/\D/g, "");

  // Check if number starts with any of the valid mobile prefixes
  return country.mobile_prefixes.some((prefix) =>
    cleanNumber.startsWith(prefix)
  );
}

/**
 * Get mobile prefixes for a country by dial code
 * @param dialCode - Country dial code (e.g., "+20")
 * @returns Array of valid mobile prefixes or empty array
 */
export function getMobilePrefixesByDialCode(dialCode: string): string[] {
  const country = countries.find((c) => c.dial_code === dialCode);
  return country && country.mobile_prefixes ? country.mobile_prefixes : [];
}

/**
 * Validate if a phone number starts with valid landline prefix for the country
 * @param phoneNumber - Phone number without country code
 * @param dialCode - Country dial code (e.g., "+20")
 * @returns Boolean indicating if the number has a valid landline prefix
 */
export function isValidLandlinePrefix(
  phoneNumber: string,
  dialCode: string
): boolean {
  const country = countries.find((c) => c.dial_code === dialCode);
  if (
    !country ||
    !country.landline_prefixes ||
    country.landline_prefixes.length === 0
  ) {
    return false; // If no landline prefixes defined, it's not a valid landline
  }

  // Remove spaces and non-digit characters from phone number
  const cleanNumber = phoneNumber.replace(/\D/g, "");

  // Check if number starts with any of the valid landline prefixes
  return country.landline_prefixes.some((prefix) =>
    cleanNumber.startsWith(prefix)
  );
}

/**
 * Get landline prefixes for a country by dial code
 * @param dialCode - Country dial code (e.g., "+20")
 * @returns Array of valid landline prefixes or empty array
 */
export function getLandlinePrefixesByDialCode(dialCode: string): string[] {
  const country = countries.find((c) => c.dial_code === dialCode);
  return country && country.landline_prefixes ? country.landline_prefixes : [];
}

/**
 * Auto-detect phone type (mobile, landline, or unknown) based on number and country
 * @param phoneNumber - Phone number without country code
 * @param dialCode - Country dial code (e.g., "+20")
 * @returns 'mobile' | 'landline' | 'unknown'
 */
export function getPhoneType(
  phoneNumber: string,
  dialCode: string
): "mobile" | "landline" | "unknown" {
  const country = countries.find((c) => c.dial_code === dialCode);
  if (!country) return "unknown";

  // Remove spaces and non-digit characters from phone number
  const cleanNumber = phoneNumber.replace(/\D/g, "");

  // For countries where area codes are shared (like USA/Canada), return unknown
  if (country.shared_area_codes) {
    return "unknown";
  }

  // Check mobile prefixes first (usually more specific)
  if (country.mobile_prefixes && country.mobile_prefixes.length > 0) {
    if (
      country.mobile_prefixes.some((prefix) => cleanNumber.startsWith(prefix))
    ) {
      return "mobile";
    }
  }

  // Check landline prefixes
  if (country.landline_prefixes && country.landline_prefixes.length > 0) {
    if (
      country.landline_prefixes.some((prefix) => cleanNumber.startsWith(prefix))
    ) {
      return "landline";
    }
  }

  return "unknown";
}

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
  phoneType?: "mobile" | "landline" | "unknown";
}

/**
 * Validate phone number by type
 * @param phoneNumber - Phone number without country code
 * @param dialCode - Country dial code (e.g., "+20")
 * @param phoneType - Type of phone ('mobile', 'landline', or 'auto')
 * @returns ValidationResult with details
 */
export function validatePhoneByType(
  phoneNumber: string,
  dialCode: string,
  phoneType: "mobile" | "landline" | "auto"
): ValidationResult {
  const country = countries.find((c) => c.dial_code === dialCode);
  if (!country) {
    return { isValid: false, errorMessage: "Invalid country code" };
  }

  // Remove spaces and non-digit characters from phone number
  const cleanNumber = phoneNumber.replace(/\D/g, "");

  // If empty, it's valid (for optional fields)
  if (!cleanNumber) {
    return { isValid: true };
  }

  // Auto-detect type if needed
  let detectedType: "mobile" | "landline" | "unknown" = "unknown";
  if (phoneType === "auto") {
    detectedType = getPhoneType(phoneNumber, dialCode);
    if (detectedType === "unknown" && !country.shared_area_codes) {
      return {
        isValid: false,
        errorMessage: "Cannot determine phone type.",
        phoneType: detectedType,
      };
    }
  } else {
    detectedType = phoneType;
  }

  // For countries with shared area codes (like USA/Canada), validate format only
  if (country.shared_area_codes) {
    const expectedLength = country.phone_lengths
      ? country.phone_lengths[0]
      : 10;
    if (cleanNumber.length !== expectedLength) {
      return {
        isValid: false,
        errorMessage: `Phone number must be ${expectedLength} digits`,
        phoneType: "unknown",
      };
    }
    return { isValid: true, phoneType: "unknown" };
  }

  // Validate based on type
  if (detectedType === "mobile") {
    // Check mobile prefix
    if (!isValidMobilePrefix(phoneNumber, dialCode)) {
      const mobilePrefixes = getMobilePrefixesByDialCode(dialCode);
      return {
        isValid: false,
        errorMessage: `Mobile number must start with: ${mobilePrefixes
          .slice(0, 5)
          .join(", ")}${mobilePrefixes.length > 5 ? "..." : ""}`,
        phoneType: "mobile",
      };
    }

    // Check mobile length
    const expectedLengths = country.mobile_lengths ||
      country.phone_lengths || [10];
    if (!expectedLengths.includes(cleanNumber.length)) {
      return {
        isValid: false,
        errorMessage: `Mobile number must be ${expectedLengths.join(
          " or "
        )} digits`,
        phoneType: "mobile",
      };
    }
  } else if (detectedType === "landline") {
    // Check landline prefix
    if (!isValidLandlinePrefix(phoneNumber, dialCode)) {
      const landlinePrefixes = getLandlinePrefixesByDialCode(dialCode);
      return {
        isValid: false,
        errorMessage: `Landline number must start with: ${landlinePrefixes
          .slice(0, 5)
          .join(", ")}${landlinePrefixes.length > 5 ? "..." : ""}`,
        phoneType: "landline",
      };
    }

    // Check landline length
    const expectedLengths = country.landline_lengths ||
      country.phone_lengths || [8, 9];
    if (!expectedLengths.includes(cleanNumber.length)) {
      return {
        isValid: false,
        errorMessage: `Landline number must be ${expectedLengths.join(
          " or "
        )} digits`,
        phoneType: "landline",
      };
    }
  }

  return { isValid: true, phoneType: detectedType };
}

/**
 * Get phone type with confidence level
 * Returns the detected phone type along with a confidence score
 */
export function getPhoneTypeWithConfidence(
  phoneNumber: string,
  dialCode: string
): {
  type: "mobile" | "landline" | "unknown";
  confidence: number; // 0-100
  reason?: string;
} {
  const country = countries.find((c) => c.dial_code === dialCode);
  if (!country) {
    return { type: "unknown", confidence: 0, reason: "Country not found" };
  }

  // Remove spaces and non-digit characters from phone number
  const cleanNumber = phoneNumber.replace(/\D/g, "");

  // If the number is too short to determine type
  if (cleanNumber.length < 3) {
    return {
      type: "unknown",
      confidence: 0,
      reason: "Number too short to determine type",
    };
  }

  // For countries where area codes are shared (like USA/Canada)
  if (country.shared_area_codes) {
    return {
      type: "unknown",
      confidence: 0,
      reason:
        "This country uses shared area codes for mobile and landline numbers",
    };
  }

  // Check mobile prefixes
  let isMobile = false;
  let isLandline = false;

  if (country.mobile_prefixes && country.mobile_prefixes.length > 0) {
    isMobile = country.mobile_prefixes.some((prefix) =>
      cleanNumber.startsWith(prefix)
    );
  }

  if (country.landline_prefixes && country.landline_prefixes.length > 0) {
    isLandline = country.landline_prefixes.some((prefix) =>
      cleanNumber.startsWith(prefix)
    );
  }

  // Calculate confidence based on various factors
  if (isMobile && !isLandline) {
    // High confidence when prefix clearly indicates mobile
    const lengthValid = country.mobile_lengths
      ? country.mobile_lengths.includes(cleanNumber.length)
      : country.phone_lengths &&
        country.phone_lengths.includes(cleanNumber.length);

    const confidence = lengthValid ? 95 : 70;
    return {
      type: "mobile",
      confidence,
      reason: "Prefix matches mobile pattern",
    };
  }

  if (isLandline && !isMobile) {
    // High confidence when prefix clearly indicates landline
    const lengthValid = country.landline_lengths
      ? country.landline_lengths.includes(cleanNumber.length)
      : country.phone_lengths &&
        country.phone_lengths.includes(cleanNumber.length);

    const confidence = lengthValid ? 95 : 70;
    return {
      type: "landline",
      confidence,
      reason: "Prefix matches landline pattern",
    };
  }

  if (isMobile && isLandline) {
    // Low confidence when prefix could be either
    return {
      type: "unknown",
      confidence: 30,
      reason: "Prefix could be mobile or landline",
    };
  }

  // No prefix match found
  return {
    type: "unknown",
    confidence: 10,
    reason: "No matching prefix found",
  };
}

const countries = [
  {
    name: "Afghanistan",
    flag: "🇦🇫",
    code: "AF",
    dial_code: "+93",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d{4}\\s?\\d{4}",
    mobile_prefixes: [
      "70",
      "71",
      "72",
      "73",
      "74",
      "75",
      "76",
      "77",
      "78",
      "79",
    ],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Åland Islands",
    flag: "🇦🇽",
    code: "AX",
    dial_code: "+358",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d{2}\\s?\\d{3}\\s?\\d{4}",
    mobile_prefixes: ["4", "50"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Albania",
    flag: "🇦🇱",
    code: "AL",
    dial_code: "+355",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d{2}\\s?\\d{3}\\s?\\d{4}",
    mobile_prefixes: ["66", "67", "68", "69"],
    mobile_lengths: [9],
    landline_lengths: [8],
    phone_lengths: [8, 9],
  },
  {
    name: "Algeria",
    flag: "🇩🇿",
    code: "DZ",
    dial_code: "+213",
    phone_placeholder: "123 45 67 89",
    phone_pattern: "\\d{3}\\s?\\d{2}\\s?\\d{2}\\s?\\d{2}",
    mobile_prefixes: ["5", "6", "7"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "American Samoa",
    flag: "🇦🇸",
    code: "AS",
    dial_code: "+1684",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d{3}\\s?\\d{4}",
    mobile_prefixes: ["684"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Andorra",
    flag: "🇦🇩",
    code: "AD",
    dial_code: "+376",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3", "6"],
    mobile_lengths: [6],
    landline_lengths: [6],
    phone_lengths: [6],
  },
  {
    name: "Angola",
    flag: "🇦🇴",
    code: "AO",
    dial_code: "+244",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["91", "92", "93", "94", "95", "96", "97", "98", "99"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Anguilla",
    flag: "🇦🇮",
    code: "AI",
    dial_code: "+1264",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["264"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Antarctica",
    flag: "🇦🇶",
    code: "AQ",
    dial_code: "+672",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [],
    mobile_lengths: [6],
    landline_lengths: [6],
    phone_lengths: [6],
  },
  {
    name: "Antigua and Barbuda",
    flag: "🇦🇬",
    code: "AG",
    dial_code: "+1268",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["268"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Argentina",
    flag: "🇦🇷",
    code: "AR",
    dial_code: "+54",
    phone_placeholder: "12 3456 7890",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["11", "15"],
    mobile_lengths: [10],
    landline_lengths: [10],
    phone_lengths: [10],
  },
  {
    name: "Armenia",
    flag: "🇦🇲",
    code: "AM",
    dial_code: "+374",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: [
      "41",
      "43",
      "44",
      "45",
      "46",
      "47",
      "48",
      "49",
      "55",
      "77",
      "91",
      "93",
      "94",
      "95",
      "96",
      "97",
      "98",
      "99",
    ],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Aruba",
    flag: "🇦🇼",
    code: "AW",
    dial_code: "+297",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["56", "59", "64", "66", "69", "73", "74", "99"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Australia",
    flag: "🇦🇺",
    code: "AU",
    dial_code: "+61",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["4"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Austria",
    flag: "🇦🇹",
    code: "AT",
    dial_code: "+43",
    phone_placeholder: "1234 567890",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: [
      "644",
      "650",
      "651",
      "652",
      "653",
      "655",
      "657",
      "659",
      "664",
      "665",
      "666",
      "667",
      "668",
      "669",
      "676",
      "677",
      "678",
      "680",
      "681",
      "688",
      "699",
    ],
    mobile_lengths: [10],
    landline_lengths: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
    phone_lengths: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
  },
  {
    name: "Azerbaijan",
    flag: "🇦🇿",
    code: "AZ",
    dial_code: "+994",
    phone_placeholder: "12 345 67 89",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["40", "50", "51", "55", "60", "70", "77", "99"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Bahamas",
    flag: "🇧🇸",
    code: "BS",
    dial_code: "+1242",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["242"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Bahrain",
    flag: "🇧🇭",
    code: "BH",
    dial_code: "+973",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3"],
    landline_prefixes: ["1"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
    phone_types: ["mobile", "landline"],
  },
  {
    name: "Bangladesh",
    flag: "🇧🇩",
    code: "BD",
    dial_code: "+880",
    phone_placeholder: "1234 567890",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["13", "14", "15", "16", "17", "18", "19"],
    mobile_lengths: [10],
    landline_lengths: [9, 10],
    phone_lengths: [9, 10],
  },
  {
    name: "Barbados",
    flag: "🇧🇧",
    code: "BB",
    dial_code: "+1246",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["246"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Belarus",
    flag: "🇧🇾",
    code: "BY",
    dial_code: "+375",
    phone_placeholder: "12 345 67 89",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["25", "29", "33", "44"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Belgium",
    flag: "🇧🇪",
    code: "BE",
    dial_code: "+32",
    phone_placeholder: "123 45 67 89",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_lengths: [9],
    landline_lengths: [8, 9],
    phone_lengths: [8, 9],
    mobile_prefixes: [
      "460",
      "461",
      "462",
      "463",
      "464",
      "465",
      "466",
      "467",
      "468",
      "469",
      "470",
      "471",
      "472",
      "473",
      "474",
      "475",
      "476",
      "477",
      "478",
      "479",
      "485",
      "486",
      "487",
      "488",
      "489",
      "490",
      "491",
      "492",
      "493",
      "494",
      "495",
      "496",
      "497",
      "498",
      "499",
    ],
  },
  {
    name: "Belize",
    flag: "🇧🇿",
    code: "BZ",
    dial_code: "+501",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Benin",
    flag: "🇧🇯",
    code: "BJ",
    dial_code: "+229",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5", "6", "9"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Bermuda",
    flag: "🇧🇲",
    code: "BM",
    dial_code: "+1441",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["441"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Bhutan",
    flag: "🇧🇹",
    code: "BT",
    dial_code: "+975",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["17"],
    mobile_lengths: [8],
    landline_lengths: [7, 8],
    phone_lengths: [7, 8],
  },
  {
    name: "Bolivia, Plurinational State of bolivia",
    flag: "🇧🇴",
    code: "BO",
    dial_code: "+591",
    phone_placeholder: "1 234 5678",
    phone_pattern: "\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Bosnia and Herzegovina",
    flag: "🇧🇦",
    code: "BA",
    dial_code: "+387",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["60", "61", "62", "63", "64", "65", "66", "67", "68"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Botswana",
    flag: "🇧🇼",
    code: "BW",
    dial_code: "+267",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["71", "72", "73", "74", "75", "76", "77"],
    mobile_lengths: [8],
    landline_lengths: [7],
    phone_lengths: [7, 8],
  },
  {
    name: "Bouvet Island",
    flag: "🇧🇻",
    code: "BV",
    dial_code: "+47",
    phone_placeholder: "123 45 678",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: [],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Brazil",
    flag: "🇧🇷",
    code: "BR",
    dial_code: "+55",
    phone_placeholder: "12 34567 8901",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_lengths: [11],
    landline_lengths: [10],
    phone_lengths: [10, 11],
    mobile_prefixes: [
      "11",
      "12",
      "13",
      "14",
      "15",
      "16",
      "17",
      "18",
      "19",
      "21",
      "22",
      "24",
      "27",
      "28",
      "31",
      "32",
      "33",
      "34",
      "35",
      "37",
      "38",
      "41",
      "42",
      "43",
      "44",
      "45",
      "46",
      "47",
      "48",
      "49",
      "51",
      "53",
      "54",
      "55",
      "61",
      "62",
      "63",
      "64",
      "65",
      "66",
      "67",
      "68",
      "69",
      "71",
      "73",
      "74",
      "75",
      "77",
      "79",
      "81",
      "82",
      "83",
      "84",
      "85",
      "86",
      "87",
      "88",
      "89",
      "91",
      "93",
      "94",
      "95",
      "96",
      "97",
      "98",
      "99",
    ],
  },
  {
    name: "British Indian Ocean Territory",
    flag: "🇮🇴",
    code: "IO",
    dial_code: "+246",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Brunei Darussalam",
    flag: "🇧🇳",
    code: "BN",
    dial_code: "+673",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7", "8"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Bulgaria",
    flag: "🇧🇬",
    code: "BG",
    dial_code: "+359",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["87", "88", "89", "98", "99"],
    mobile_lengths: [9],
    landline_lengths: [8],
    phone_lengths: [8, 9],
  },
  {
    name: "Burkina Faso",
    flag: "🇧🇫",
    code: "BF",
    dial_code: "+226",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Burundi",
    flag: "🇧🇮",
    code: "BI",
    dial_code: "+257",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [
      "29",
      "31",
      "61",
      "68",
      "69",
      "71",
      "72",
      "75",
      "76",
      "77",
      "78",
      "79",
    ],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Cambodia",
    flag: "🇰🇭",
    code: "KH",
    dial_code: "+855",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: [
      "10",
      "11",
      "12",
      "14",
      "15",
      "16",
      "17",
      "18",
      "31",
      "60",
      "61",
      "66",
      "67",
      "68",
      "69",
      "70",
      "71",
      "76",
      "77",
      "78",
      "79",
      "81",
      "85",
      "86",
      "87",
      "88",
      "89",
      "90",
      "92",
      "93",
      "95",
      "96",
      "97",
      "98",
      "99",
    ],
    mobile_lengths: [8, 9],
    landline_lengths: [8, 9],
    phone_lengths: [8, 9],
  },
  {
    name: "Cameroon",
    flag: "🇨🇲",
    code: "CM",
    dial_code: "+237",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Canada",
    flag: "🇨🇦",
    code: "CA",
    dial_code: "+1",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [
      '403', '587', '780', '825', '368', '236', '250', '604', '672', '778', '204', '431', '584', '428', '506',
      '709', '867', '782', '902', '226', '249', '289', '343', '365', '416', '437', '519', '548', '613', '647',
      '705', '807', '905', '367', '418', '438', '450', '468', '514', '579', '581', '819', '873', '306', '474',
      '639', '753',
    ],
    landline_prefixes: [],
    shared_area_codes: true,
    phone_lengths: [10],
    phone_types: ["mobile", "landline"],
  },
  {
    name: "Cape Verde",
    flag: "🇨🇻",
    code: "CV",
    dial_code: "+238",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5", "9"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Cayman Islands",
    flag: "🇰🇾",
    code: "KY",
    dial_code: "+345",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["345"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Central African Republic",
    flag: "🇨🇫",
    code: "CF",
    dial_code: "+236",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Chad",
    flag: "🇹🇩",
    code: "TD",
    dial_code: "+235",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7", "9"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Chile",
    flag: "🇨🇱",
    code: "CL",
    dial_code: "+56",
    phone_placeholder: "1 2345 6789",
    phone_pattern: "\\d\\s?\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "China",
    flag: "🇨🇳",
    code: "CN",
    dial_code: "+86",
    phone_placeholder: "123 4567 8901",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_lengths: [11],
    landline_lengths: [10, 11],
    phone_lengths: [10, 11],
    mobile_prefixes: [
      "130",
      "131",
      "132",
      "133",
      "134",
      "135",
      "136",
      "137",
      "138",
      "139",
      "147",
      "150",
      "151",
      "152",
      "153",
      "155",
      "156",
      "157",
      "158",
      "159",
      "180",
      "181",
      "182",
      "183",
      "184",
      "185",
      "186",
      "187",
      "188",
      "189",
      "198",
      "199",
    ],
  },
  {
    name: "Christmas Island",
    flag: "🇨🇽",
    code: "CX",
    dial_code: "+61",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["4"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Cocos (Keeling) Islands",
    flag: "🇨🇨",
    code: "CC",
    dial_code: "+61",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["4"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Colombia",
    flag: "🇨🇴",
    code: "CO",
    dial_code: "+57",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [
      "300",
      "301",
      "302",
      "303",
      "304",
      "305",
      "310",
      "311",
      "312",
      "313",
      "314",
      "315",
      "316",
      "317",
      "318",
      "319",
      "320",
      "321",
      "322",
      "323",
      "324",
      "350",
      "351",
    ],
    mobile_lengths: [10],
    landline_lengths: [8],
    phone_lengths: [8, 10],
  },
  {
    name: "Comoros",
    flag: "🇰🇲",
    code: "KM",
    dial_code: "+269",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Congo",
    flag: "🇨🇬",
    code: "CG",
    dial_code: "+242",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["0"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Congo, The Democratic Republic of the Congo",
    flag: "🇨🇩",
    code: "CD",
    dial_code: "+243",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["8", "9"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Cook Islands",
    flag: "🇨🇰",
    code: "CK",
    dial_code: "+682",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5", "7"],
    mobile_lengths: [5],
    landline_lengths: [5],
    phone_lengths: [5],
  },
  {
    name: "Costa Rica",
    flag: "🇨🇷",
    code: "CR",
    dial_code: "+506",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7", "8"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Cote d'Ivoire",
    flag: "🇨🇮",
    code: "CI",
    dial_code: "+225",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["0", "4", "5", "6"],
    mobile_lengths: [10],
    landline_lengths: [10],
    phone_lengths: [10],
  },
  {
    name: "Croatia",
    flag: "🇭🇷",
    code: "HR",
    dial_code: "+385",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["91", "92", "95", "97", "98", "99"],
    mobile_lengths: [8, 9],
    landline_lengths: [8, 9],
    phone_lengths: [8, 9],
  },
  {
    name: "Cuba",
    flag: "🇨🇺",
    code: "CU",
    dial_code: "+53",
    phone_placeholder: "1 234 5678",
    phone_pattern: "\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Curaçao",
    flag: "🇨🇼",
    code: "CW",
    dial_code: "+5999",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Cyprus",
    flag: "🇨🇾",
    code: "CY",
    dial_code: "+357",
    phone_placeholder: "12 345678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["95", "96", "97", "99"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Czech Republic",
    flag: "🇨🇿",
    code: "CZ",
    dial_code: "+420",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: [
      "601",
      "602",
      "603",
      "604",
      "605",
      "606",
      "607",
      "608",
      "702",
      "720",
      "721",
      "722",
      "723",
      "724",
      "725",
      "726",
      "727",
      "728",
      "729",
      "730",
      "731",
      "732",
      "733",
      "734",
      "735",
      "736",
      "737",
      "738",
      "739",
      "770",
      "771",
      "772",
      "773",
      "774",
      "775",
      "776",
      "777",
      "778",
      "779",
    ],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Denmark",
    flag: "🇩🇰",
    code: "DK",
    dial_code: "+45",
    phone_placeholder: "12 34 56 78",
    phone_pattern: "\\d\\d\\s?\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: [
      "2",
      "30",
      "31",
      "40",
      "41",
      "42",
      "50",
      "51",
      "52",
      "53",
      "60",
      "61",
      "71",
      "81",
      "91",
      "92",
      "93",
    ],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Djibouti",
    flag: "🇩🇯",
    code: "DJ",
    dial_code: "+253",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["77"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Dominica",
    flag: "🇩🇲",
    code: "DM",
    dial_code: "+1767",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["767"],
    mobile_lengths: [7],
    landline_lengths: [7],
    phone_lengths: [7],
  },
  {
    name: "Dominican Republic",
    flag: "🇩🇴",
    code: "DO",
    dial_code: "+1849",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["809", "829", "849"],
    mobile_lengths: [10],
    landline_lengths: [10],
    phone_lengths: [10],
  },
  {
    name: "Ecuador",
    flag: "🇪🇨",
    code: "EC",
    dial_code: "+593",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["8", "9"],
    mobile_lengths: [9],
    landline_lengths: [8],
    phone_lengths: [8, 9],
  },
  {
    name: "Egypt",
    flag: "🇪🇬",
    code: "EG",
    dial_code: "+20",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d{1,3}\\s?\\d{3,4}\\s?\\d{4}",
    mobile_lengths: [11, 10],
    landline_lengths: [8, 9, 10],
    phone_lengths: [8, 9, 10, 11],
    mobile_prefixes: ["010", "011", "012", "015", "10", "11", "12", "15"],
    landline_prefixes: [
      "2",
      "3",
      "4",
      "13",
      "15",
      "40",
      "45",
      "46",
      "47",
      "48",
      "50",
      "55",
      "56",
      "57",
      "62",
      "64",
      "65",
      "66",
      "68",
      "69",
      "82",
      "84",
      "86",
      "88",
      "92",
      "93",
      "95",
      "96",
      "97",
      "02",
      "03",
      "04",
      "013",
      "015",
      "040",
      "045",
      "046",
      "047",
      "048",
      "050",
      "055",
      "056",
      "057",
      "062",
      "064",
      "065",
      "066",
      "068",
      "069",
      "082",
      "084",
      "086",
      "088",
      "092",
      "093",
      "095",
      "096",
      "097",
    ],
    phone_types: ["mobile", "landline"],
  },
  {
    name: "El Salvador",
    flag: "🇸🇻",
    code: "SV",
    dial_code: "+503",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Equatorial Guinea",
    flag: "🇬🇶",
    code: "GQ",
    dial_code: "+240",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["222", "333", "551", "555"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Eritrea",
    flag: "🇪🇷",
    code: "ER",
    dial_code: "+291",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7", "8"],
    mobile_lengths: [7],
    landline_lengths: [6, 7],
    phone_lengths: [6, 7],
  },
  {
    name: "Estonia",
    flag: "🇪🇪",
    code: "EE",
    dial_code: "+372",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5"],
    mobile_lengths: [7, 8],
    landline_lengths: [7, 8],
    phone_lengths: [7, 8],
  },
  {
    name: "Ethiopia",
    flag: "🇪🇹",
    code: "ET",
    dial_code: "+251",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Falkland Islands (Malvinas)",
    flag: "🇫🇰",
    code: "FK",
    dial_code: "+500",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [],
  },
  {
    name: "Faroe Islands",
    flag: "🇫🇴",
    code: "FO",
    dial_code: "+298",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["2", "5", "7"],
  },
  {
    name: "Fiji",
    flag: "🇫🇯",
    code: "FJ",
    dial_code: "+679",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7", "8", "9"],
  },
  {
    name: "Finland",
    flag: "🇫🇮",
    code: "FI",
    dial_code: "+358",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["4", "50"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "France",
    flag: "🇫🇷",
    code: "FR",
    dial_code: "+33",
    phone_placeholder: "1 23 45 67 89",
    phone_pattern: "\\d\\s?\\d\\d\\s?\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["6", "7"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "French Guiana",
    flag: "🇬🇫",
    code: "GF",
    dial_code: "+594",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["694"],
  },
  {
    name: "French Polynesia",
    flag: "🇵🇫",
    code: "PF",
    dial_code: "+689",
    phone_placeholder: "12 34 56 78",
    phone_pattern: "\\d\\d\\s?\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["8"],
  },
  {
    name: "French Southern Territories",
    flag: "🇹🇫",
    code: "TF",
    dial_code: "+262",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [],
  },
  {
    name: "Gabon",
    flag: "🇬🇦",
    code: "GA",
    dial_code: "+241",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["0"],
  },
  {
    name: "Gambia",
    flag: "🇬🇲",
    code: "GM",
    dial_code: "+220",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["2", "3", "7", "9"],
  },
  {
    name: "Georgia",
    flag: "🇬🇪",
    code: "GE",
    dial_code: "+995",
    phone_placeholder: "123 45 67 89",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["5"],
  },
  {
    name: "Germany",
    flag: "🇩🇪",
    code: "DE",
    dial_code: "+49",
    phone_placeholder: "123 45678901",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["15", "16", "17"],
    mobile_lengths: [11, 12],
    landline_lengths: [10, 11, 12],
    phone_lengths: [10, 11, 12],
  },
  {
    name: "Ghana",
    flag: "🇬🇭",
    code: "GH",
    dial_code: "+233",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: [
      "20",
      "23",
      "24",
      "25",
      "26",
      "27",
      "28",
      "50",
      "54",
      "55",
      "56",
      "57",
      "59",
    ],
  },
  {
    name: "Gibraltar",
    flag: "🇬🇮",
    code: "GI",
    dial_code: "+350",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5", "6"],
  },
  {
    name: "Greece",
    flag: "🇬🇷",
    code: "GR",
    dial_code: "+30",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["69"],
  },
  {
    name: "Greenland",
    flag: "🇬🇱",
    code: "GL",
    dial_code: "+299",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["4", "5", "55"],
  },
  {
    name: "Grenada",
    flag: "🇬🇩",
    code: "GD",
    dial_code: "+1473",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["473"],
  },
  {
    name: "Guadeloupe",
    flag: "🇬🇵",
    code: "GP",
    dial_code: "+590",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["690"],
  },
  {
    name: "Guam",
    flag: "🇬🇺",
    code: "GU",
    dial_code: "+1671",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["671"],
  },
  {
    name: "Guatemala",
    flag: "🇬🇹",
    code: "GT",
    dial_code: "+502",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3", "4", "5"],
  },
  {
    name: "Guernsey",
    flag: "🇬🇬",
    code: "GG",
    dial_code: "+44",
    phone_placeholder: "1234 567890",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Guinea",
    flag: "🇬🇳",
    code: "GN",
    dial_code: "+224",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6"],
  },
  {
    name: "Guinea-Bissau",
    flag: "🇬🇼",
    code: "GW",
    dial_code: "+245",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5", "6", "7"],
  },
  {
    name: "Guyana",
    flag: "🇬🇾",
    code: "GY",
    dial_code: "+592",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6"],
  },
  {
    name: "Haiti",
    flag: "🇭🇹",
    code: "HT",
    dial_code: "+509",
    phone_placeholder: "12 34 5678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3", "4"],
  },
  {
    name: "Heard Island and Mcdonald Islands",
    flag: "🇭🇲",
    code: "HM",
    dial_code: "+672",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [],
  },
  {
    name: "Holy See (Vatican City State)",
    flag: "🇻🇦",
    code: "VA",
    dial_code: "+379",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3"],
  },
  {
    name: "Honduras",
    flag: "🇭🇳",
    code: "HN",
    dial_code: "+504",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3", "7", "8", "9"],
  },
  {
    name: "Hong Kong",
    flag: "🇭🇰",
    code: "HK",
    dial_code: "+852",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5", "6", "9"],
  },
  {
    name: "Hungary",
    flag: "🇭🇺",
    code: "HU",
    dial_code: "+36",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["20", "30", "31", "50", "70"],
  },
  {
    name: "Iceland",
    flag: "🇮🇸",
    code: "IS",
    dial_code: "+354",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7", "8"],
  },
  {
    name: "India",
    flag: "🇮🇳",
    code: "IN",
    dial_code: "+91",
    phone_placeholder: "12345 67890",
    phone_pattern: "\\d\\d\\d\\d\\d\\s?\\d\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7", "8", "9"],
    mobile_lengths: [10],
    landline_lengths: [10, 11],
    phone_lengths: [10, 11],
  },
  {
    name: "Indonesia",
    flag: "🇮🇩",
    code: "ID",
    dial_code: "+62",
    phone_placeholder: "123 4567 8901",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["8"],
  },
  {
    name: "Iran, Islamic Republic of Persian Gulf",
    flag: "🇮🇷",
    code: "IR",
    dial_code: "+98",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Iraq",
    flag: "🇮🇶",
    code: "IQ",
    dial_code: "+964",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Ireland",
    flag: "🇮🇪",
    code: "IE",
    dial_code: "+353",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["83", "85", "86", "87", "88", "89"],
  },
  {
    name: "Isle of Man",
    flag: "🇮🇲",
    code: "IM",
    dial_code: "+44",
    phone_placeholder: "1234 567890",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Israel",
    flag: "🇮🇱",
    code: "IL",
    dial_code: "+972",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5"],
  },
  {
    name: "Italy",
    flag: "🇮🇹",
    code: "IT",
    dial_code: "+39",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3"],
    mobile_lengths: [10],
    landline_lengths: [9, 10],
    phone_lengths: [9, 10],
  },
  {
    name: "Jamaica",
    flag: "🇯🇲",
    code: "JM",
    dial_code: "+1876",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["876"],
  },
  {
    name: "Japan",
    flag: "🇯🇵",
    code: "JP",
    dial_code: "+81",
    phone_placeholder: "12 3456 7890",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["70", "80", "90"],
    mobile_lengths: [10, 11],
    landline_lengths: [10],
    phone_lengths: [10, 11],
  },
  {
    name: "Jersey",
    flag: "🇯🇪",
    code: "JE",
    dial_code: "+44",
    phone_placeholder: "1234 567890",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Jordan",
    flag: "🇯🇴",
    code: "JO",
    dial_code: "+962",
    phone_placeholder: "1 2345 6789",
    phone_pattern: "\\d\\s?\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Kazakhstan",
    flag: "🇰🇿",
    code: "KZ",
    dial_code: "+7",
    phone_placeholder: "123 456 78 90",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["70", "71", "72", "73", "74", "75", "76", "77", "78"],
  },
  {
    name: "Kenya",
    flag: "🇰🇪",
    code: "KE",
    dial_code: "+254",
    phone_placeholder: "123 456789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Kiribati",
    flag: "🇰🇮",
    code: "KI",
    dial_code: "+686",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7", "9"],
  },
  {
    name: "Korea, Democratic People's Republic of Korea",
    flag: "🇰🇵",
    code: "KP",
    dial_code: "+850",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["19"],
  },
  {
    name: "Korea, Republic of South Korea",
    flag: "🇰🇷",
    code: "KR",
    dial_code: "+82",
    phone_placeholder: "12 3456 7890",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["10", "11", "16", "17", "18", "19"],
  },
  {
    name: "Kosovo",
    flag: "🇽🇰",
    code: "XK",
    dial_code: "+383",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["43", "44", "45", "48", "49"],
  },
  {
    name: "Kuwait",
    flag: "🇰🇼",
    code: "KW",
    dial_code: "+965",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5", "6", "9"],
    landline_prefixes: ["2"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
    phone_types: ["mobile", "landline"],
  },
  {
    name: "Kyrgyzstan",
    flag: "🇰🇬",
    code: "KG",
    dial_code: "+996",
    phone_placeholder: "123 456789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["5", "7", "9"],
  },
  {
    name: "Laos",
    flag: "🇱🇦",
    code: "LA",
    dial_code: "+856",
    phone_placeholder: "12 34 567 890",
    phone_pattern: "\\d\\d\\s?\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["20"],
  },
  {
    name: "Latvia",
    flag: "🇱🇻",
    code: "LV",
    dial_code: "+371",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["2"],
  },
  {
    name: "Lebanon",
    flag: "🇱🇧",
    code: "LB",
    dial_code: "+961",
    phone_placeholder: "1 234 567",
    phone_pattern: "\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["3", "7", "8"],
  },
  {
    name: "Lesotho",
    flag: "🇱🇸",
    code: "LS",
    dial_code: "+266",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5", "6"],
  },
  {
    name: "Liberia",
    flag: "🇱🇷",
    code: "LR",
    dial_code: "+231",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["4", "5", "6", "7"],
  },
  {
    name: "Libyan Arab Jamahiriya",
    flag: "🇱🇾",
    code: "LY",
    dial_code: "+218",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Liechtenstein",
    flag: "🇱🇮",
    code: "LI",
    dial_code: "+423",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7"],
  },
  {
    name: "Lithuania",
    flag: "🇱🇹",
    code: "LT",
    dial_code: "+370",
    phone_placeholder: "123 45 678",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["6"],
  },
  {
    name: "Luxembourg",
    flag: "🇱🇺",
    code: "LU",
    dial_code: "+352",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["621", "628", "661", "671", "681", "691"],
  },
  {
    name: "Macao",
    flag: "🇲🇴",
    code: "MO",
    dial_code: "+853",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6"],
  },
  {
    name: "Macedonia",
    flag: "🇲🇰",
    code: "MK",
    dial_code: "+389",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["70", "71", "72", "75", "76", "77", "78"],
  },
  {
    name: "Madagascar",
    flag: "🇲🇬",
    code: "MG",
    dial_code: "+261",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3"],
  },
  {
    name: "Malawi",
    flag: "🇲🇼",
    code: "MW",
    dial_code: "+265",
    phone_placeholder: "123 45 67 89",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["77", "88", "99"],
  },
  {
    name: "Malaysia",
    flag: "🇲🇾",
    code: "MY",
    dial_code: "+60",
    phone_placeholder: "12 3456 7890",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [
      "10",
      "11",
      "12",
      "13",
      "14",
      "15",
      "16",
      "17",
      "18",
      "19",
    ],
  },
  {
    name: "Maldives",
    flag: "🇲🇻",
    code: "MV",
    dial_code: "+960",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7", "9"],
  },
  {
    name: "Mali",
    flag: "🇲🇱",
    code: "ML",
    dial_code: "+223",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7", "8", "9"],
  },
  {
    name: "Malta",
    flag: "🇲🇹",
    code: "MT",
    dial_code: "+356",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7", "9"],
  },
  {
    name: "Marshall Islands",
    flag: "🇲🇭",
    code: "MH",
    dial_code: "+692",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["692"],
  },
  {
    name: "Martinique",
    flag: "🇲🇶",
    code: "MQ",
    dial_code: "+596",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["696"],
  },
  {
    name: "Mauritania",
    flag: "🇲🇷",
    code: "MR",
    dial_code: "+222",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["2", "3", "4", "6"],
  },
  {
    name: "Mauritius",
    flag: "🇲🇺",
    code: "MU",
    dial_code: "+230",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5"],
  },
  {
    name: "Mayotte",
    flag: "🇾🇹",
    code: "YT",
    dial_code: "+262",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["639"],
  },
  {
    name: "Mexico",
    flag: "🇲🇽",
    code: "MX",
    dial_code: "+52",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["1"],
    mobile_lengths: [10],
    landline_lengths: [10],
    phone_lengths: [10],
  },
  {
    name: "Micronesia, Federated States of Micronesia",
    flag: "🇫🇲",
    code: "FM",
    dial_code: "+691",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["691"],
  },
  {
    name: "Moldova",
    flag: "🇲🇩",
    code: "MD",
    dial_code: "+373",
    phone_placeholder: "123 45 678",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["60", "67", "68", "69", "78", "79"],
  },
  {
    name: "Monaco",
    flag: "🇲🇨",
    code: "MC",
    dial_code: "+377",
    phone_placeholder: "12 34 56 78",
    phone_pattern: "\\d\\d\\s?\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["4", "6"],
  },
  {
    name: "Mongolia",
    flag: "🇲🇳",
    code: "MN",
    dial_code: "+976",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["8", "9"],
  },
  {
    name: "Montenegro",
    flag: "🇲🇪",
    code: "ME",
    dial_code: "+382",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["67", "68", "69"],
  },
  {
    name: "Montserrat",
    flag: "🇲🇸",
    code: "MS",
    dial_code: "+1664",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["664"],
  },
  {
    name: "Morocco",
    flag: "🇲🇦",
    code: "MA",
    dial_code: "+212",
    phone_placeholder: "123 456789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7"],
  },
  {
    name: "Mozambique",
    flag: "🇲🇿",
    code: "MZ",
    dial_code: "+258",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["8"],
  },
  {
    name: "Myanmar",
    flag: "🇲🇲",
    code: "MM",
    dial_code: "+95",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Namibia",
    flag: "🇳🇦",
    code: "NA",
    dial_code: "+264",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["60", "81", "82", "83", "85"],
  },
  {
    name: "Nauru",
    flag: "🇳🇷",
    code: "NR",
    dial_code: "+674",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["555"],
  },
  {
    name: "Nepal",
    flag: "🇳🇵",
    code: "NP",
    dial_code: "+977",
    phone_placeholder: "12 3456 7890",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["97", "98"],
  },
  {
    name: "Netherlands",
    flag: "🇳🇱",
    code: "NL",
    dial_code: "+31",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Caribbean Netherlands",
    flag: "🇧🇶",
    code: "BQ",
    dial_code: "+599",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "New Caledonia",
    flag: "🇳🇨",
    code: "NC",
    dial_code: "+687",
    phone_placeholder: "12 34 56",
    phone_pattern: "\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["7", "8", "9"],
  },
  {
    name: "New Zealand",
    flag: "🇳🇿",
    code: "NZ",
    dial_code: "+64",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["2"],
  },
  {
    name: "Nicaragua",
    flag: "🇳🇮",
    code: "NI",
    dial_code: "+505",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5", "7", "8"],
  },
  {
    name: "Niger",
    flag: "🇳🇪",
    code: "NE",
    dial_code: "+227",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["8", "9"],
  },
  {
    name: "Nigeria",
    flag: "🇳🇬",
    code: "NG",
    dial_code: "+234",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7", "8", "9"],
  },
  {
    name: "Niue",
    flag: "🇳🇺",
    code: "NU",
    dial_code: "+683",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["683"],
  },
  {
    name: "Norfolk Island",
    flag: "🇳🇫",
    code: "NF",
    dial_code: "+672",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3", "5"],
  },
  {
    name: "Northern Mariana Islands",
    flag: "🇲🇵",
    code: "MP",
    dial_code: "+1670",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["670"],
  },
  {
    name: "Norway",
    flag: "🇳🇴",
    code: "NO",
    dial_code: "+47",
    phone_placeholder: "123 45 678",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["4", "9"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
  },
  {
    name: "Oman",
    flag: "🇴🇲",
    code: "OM",
    dial_code: "+968",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
    landline_prefixes: ["2"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
    phone_types: ["mobile", "landline"],
  },
  {
    name: "Pakistan",
    flag: "🇵🇰",
    code: "PK",
    dial_code: "+92",
    phone_placeholder: "123 4567890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: [
      "30",
      "31",
      "32",
      "33",
      "34",
      "35",
      "36",
      "37",
      "38",
      "39",
    ],
  },
  {
    name: "Palau",
    flag: "🇵🇼",
    code: "PW",
    dial_code: "+680",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["680"],
  },
  {
    name: "Palestinian Territory, Occupied",
    flag: "🇵🇸",
    code: "PS",
    dial_code: "+970",
    phone_placeholder: "123 45 6789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5"],
  },
  {
    name: "Panama",
    flag: "🇵🇦",
    code: "PA",
    dial_code: "+507",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6"],
  },
  {
    name: "Papua New Guinea",
    flag: "🇵🇬",
    code: "PG",
    dial_code: "+675",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Paraguay",
    flag: "🇵🇾",
    code: "PY",
    dial_code: "+595",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Peru",
    flag: "🇵🇪",
    code: "PE",
    dial_code: "+51",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Philippines",
    flag: "🇵🇭",
    code: "PH",
    dial_code: "+63",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Pitcairn",
    flag: "🇵🇳",
    code: "PN",
    dial_code: "+64",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["2"],
  },
  {
    name: "Poland",
    flag: "🇵🇱",
    code: "PL",
    dial_code: "+48",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: [
      "45",
      "50",
      "51",
      "53",
      "57",
      "60",
      "66",
      "69",
      "72",
      "73",
      "78",
      "79",
      "88",
    ],
  },
  {
    name: "Portugal",
    flag: "🇵🇹",
    code: "PT",
    dial_code: "+351",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Puerto Rico",
    flag: "🇵🇷",
    code: "PR",
    dial_code: "+1939",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["787", "939"],
  },
  {
    name: "Qatar",
    flag: "🇶🇦",
    code: "QA",
    dial_code: "+974",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3", "5", "6", "7"],
    landline_prefixes: ["4"],
    mobile_lengths: [8],
    landline_lengths: [8],
    phone_lengths: [8],
    phone_types: ["mobile", "landline"],
  },
  {
    name: "Romania",
    flag: "🇷🇴",
    code: "RO",
    dial_code: "+40",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Russia",
    flag: "🇷🇺",
    code: "RU",
    dial_code: "+7",
    phone_placeholder: "123 456 78 90",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["9"],
    mobile_lengths: [10],
    landline_lengths: [10],
    phone_lengths: [10],
  },
  {
    name: "Rwanda",
    flag: "🇷🇼",
    code: "RW",
    dial_code: "+250",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Reunion",
    flag: "🇷🇪",
    code: "RE",
    dial_code: "+262",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["692", "693"],
  },
  {
    name: "Saint Barthelemy",
    flag: "🇧🇱",
    code: "BL",
    dial_code: "+590",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["690"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Saint Helena, Ascension and Tristan Da Cunha",
    flag: "🇸🇭",
    code: "SH",
    dial_code: "+290",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [],
  },
  {
    name: "Saint Kitts and Nevis",
    flag: "🇰🇳",
    code: "KN",
    dial_code: "+1869",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["869"],
  },
  {
    name: "Saint Lucia",
    flag: "🇱🇨",
    code: "LC",
    dial_code: "+1758",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["758"],
  },
  {
    name: "Saint Martin",
    flag: "🇲🇫",
    code: "MF",
    dial_code: "+590",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["690"],
  },
  {
    name: "Saint Pierre and Miquelon",
    flag: "🇵🇲",
    code: "PM",
    dial_code: "+508",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["55"],
  },
  {
    name: "Saint Vincent and the Grenadines",
    flag: "🇻🇨",
    code: "VC",
    dial_code: "+1784",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["784"],
  },
  {
    name: "Samoa",
    flag: "🇼🇸",
    code: "WS",
    dial_code: "+685",
    phone_placeholder: "12 3456",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "San Marino",
    flag: "🇸🇲",
    code: "SM",
    dial_code: "+378",
    phone_placeholder: "1234 567890",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["6"],
  },
  {
    name: "Sao Tome and Principe",
    flag: "🇸🇹",
    code: "ST",
    dial_code: "+239",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Saudi Arabia",
    flag: "🇸🇦",
    code: "SA",
    dial_code: "+966",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [
      "50",
      "51",
      "52",
      "53",
      "54",
      "55",
      "56",
      "57",
      "58",
      "59",
    ],
    landline_prefixes: ["11", "12", "13", "14", "16", "17"],
    mobile_lengths: [9],
    landline_lengths: [8],
    phone_lengths: [8, 9],
    phone_types: ["mobile", "landline"],
  },
  {
    name: "Senegal",
    flag: "🇸🇳",
    code: "SN",
    dial_code: "+221",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Serbia",
    flag: "🇷🇸",
    code: "RS",
    dial_code: "+381",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["60", "61", "62", "63", "64", "65", "66", "69"],
  },
  {
    name: "Seychelles",
    flag: "🇸🇨",
    code: "SC",
    dial_code: "+248",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["2"],
  },
  {
    name: "Sierra Leone",
    flag: "🇸🇱",
    code: "SL",
    dial_code: "+232",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [
      "21",
      "25",
      "30",
      "31",
      "32",
      "33",
      "34",
      "35",
      "36",
      "37",
      "38",
      "39",
      "7",
      "8",
    ],
  },
  {
    name: "Singapore",
    flag: "🇸🇬",
    code: "SG",
    dial_code: "+65",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["8", "9"],
  },
  {
    name: "Sint Maarten",
    flag: "🇸🇽",
    code: "SX",
    dial_code: "+1721",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["721"],
  },
  {
    name: "Slovakia",
    flag: "🇸🇰",
    code: "SK",
    dial_code: "+421",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["90", "91", "94", "95", "96", "99"],
  },
  {
    name: "Slovenia",
    flag: "🇸🇮",
    code: "SI",
    dial_code: "+386",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [
      "30",
      "31",
      "40",
      "41",
      "51",
      "64",
      "65",
      "68",
      "69",
      "70",
      "71",
    ],
  },
  {
    name: "Solomon Islands",
    flag: "🇸🇧",
    code: "SB",
    dial_code: "+677",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7", "8"],
  },
  {
    name: "Somalia",
    flag: "🇸🇴",
    code: "SO",
    dial_code: "+252",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["6", "7"],
  },
  {
    name: "South Africa",
    flag: "🇿🇦",
    code: "ZA",
    dial_code: "+27",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7", "8"],
  },
  {
    name: "South Sudan",
    flag: "🇸🇸",
    code: "SS",
    dial_code: "+211",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "South Georgia and the South Sandwich Islands",
    flag: "🇬🇸",
    code: "GS",
    dial_code: "+500",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [],
  },
  {
    name: "Spain",
    flag: "🇪🇸",
    code: "ES",
    dial_code: "+34",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["6", "7"],
    mobile_lengths: [9],
    landline_lengths: [9],
    phone_lengths: [9],
  },
  {
    name: "Sri Lanka",
    flag: "🇱🇰",
    code: "LK",
    dial_code: "+94",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Sudan",
    flag: "🇸🇩",
    code: "SD",
    dial_code: "+249",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Suriname",
    flag: "🇸🇷",
    code: "SR",
    dial_code: "+597",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7", "8"],
  },
  {
    name: "Svalbard and Jan Mayen",
    flag: "🇸🇯",
    code: "SJ",
    dial_code: "+47",
    phone_placeholder: "123 45 678",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["4", "9"],
  },
  {
    name: "Eswatini",
    flag: "🇸🇿",
    code: "SZ",
    dial_code: "+268",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Sweden",
    flag: "🇸🇪",
    code: "SE",
    dial_code: "+46",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
    mobile_lengths: [9],
    landline_lengths: [7, 8, 9],
    phone_lengths: [7, 8, 9],
  },
  {
    name: "Switzerland",
    flag: "🇨🇭",
    code: "CH",
    dial_code: "+41",
    phone_placeholder: "12 345 67 89",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["76", "77", "78", "79"],
  },
  {
    name: "Syrian Arab Republic",
    flag: "🇸🇾",
    code: "SY",
    dial_code: "+963",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Taiwan",
    flag: "🇹🇼",
    code: "TW",
    dial_code: "+886",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Tajikistan",
    flag: "🇹🇯",
    code: "TJ",
    dial_code: "+992",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Tanzania, United Republic of Tanzania",
    flag: "🇹🇿",
    code: "TZ",
    dial_code: "+255",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "7"],
  },
  {
    name: "Thailand",
    flag: "🇹🇭",
    code: "TH",
    dial_code: "+66",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["6", "8", "9"],
  },
  {
    name: "Timor-Leste",
    flag: "🇹🇱",
    code: "TL",
    dial_code: "+670",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Togo",
    flag: "🇹🇬",
    code: "TG",
    dial_code: "+228",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Tokelau",
    flag: "🇹🇰",
    code: "TK",
    dial_code: "+690",
    phone_placeholder: "1234",
    phone_pattern: "\\d\\d\\d\\d",
    mobile_prefixes: [],
  },
  {
    name: "Tonga",
    flag: "🇹🇴",
    code: "TO",
    dial_code: "+676",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7", "8"],
  },
  {
    name: "Trinidad and Tobago",
    flag: "🇹🇹",
    code: "TT",
    dial_code: "+1868",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["868"],
  },
  {
    name: "Tunisia",
    flag: "🇹🇳",
    code: "TN",
    dial_code: "+216",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["2", "4", "5", "9"],
  },
  {
    name: "Turkey",
    flag: "🇹🇷",
    code: "TR",
    dial_code: "+90",
    phone_placeholder: "123 456 78 90",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["50", "53", "54", "55"],
    landline_prefixes: [
      "21",
      "22",
      "23",
      "24",
      "26",
      "28",
      "31",
      "32",
      "34",
      "38",
      "41",
      "42",
    ],
    mobile_lengths: [10],
    landline_lengths: [10],
    phone_lengths: [10],
    phone_types: ["mobile", "landline"],
  },
  {
    name: "Turkmenistan",
    flag: "🇹🇲",
    code: "TM",
    dial_code: "+993",
    phone_placeholder: "12 345678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["6"],
  },
  {
    name: "Turks and Caicos Islands",
    flag: "🇹🇨",
    code: "TC",
    dial_code: "+1649",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["649"],
  },
  {
    name: "Tuvalu",
    flag: "🇹🇻",
    code: "TV",
    dial_code: "+688",
    phone_placeholder: "12345",
    phone_pattern: "\\d\\d\\d\\d\\d",
    mobile_prefixes: [],
  },
  {
    name: "Uganda",
    flag: "🇺🇬",
    code: "UG",
    dial_code: "+256",
    phone_placeholder: "123 456789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Ukraine",
    flag: "🇺🇦",
    code: "UA",
    dial_code: "+380",
    phone_placeholder: "12 345 67 89",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: [
      "39",
      "50",
      "63",
      "66",
      "67",
      "68",
      "91",
      "92",
      "93",
      "94",
      "95",
      "96",
      "97",
      "98",
      "99",
    ],
  },
  {
    name: "United Arab Emirates",
    flag: "🇦🇪",
    code: "AE",
    dial_code: "+971",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["50", "52", "54", "55", "56", "58"],
    landline_prefixes: ["2", "3", "4", "6", "7", "9"],
    mobile_lengths: [9],
    landline_lengths: [8],
    phone_lengths: [8, 9],
    phone_types: ["mobile", "landline"],
  },
  {
    name: "United Kingdom",
    flag: "🇬🇧",
    code: "GB",
    dial_code: "+44",
    phone_placeholder: "1234 567890",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
    mobile_lengths: [10],
    landline_lengths: [10, 11],
    phone_lengths: [10, 11],
  },
  {
    name: "United States",
    flag: "🇺🇸",
    code: "US",
    dial_code: "+1",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: [],
    landline_prefixes: [],
    shared_area_codes: true,
    phone_lengths: [10],
    phone_types: ["mobile", "landline"],
  },
  {
    name: "Uruguay",
    flag: "🇺🇾",
    code: "UY",
    dial_code: "+598",
    phone_placeholder: "12 345 678",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Uzbekistan",
    flag: "🇺🇿",
    code: "UZ",
    dial_code: "+998",
    phone_placeholder: "12 345 67 89",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\s?\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Vanuatu",
    flag: "🇻🇺",
    code: "VU",
    dial_code: "+678",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["5", "7"],
  },
  {
    name: "Venezuela, Bolivarian Republic of Venezuela",
    flag: "🇻🇪",
    code: "VE",
    dial_code: "+58",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["4"],
  },
  {
    name: "Vietnam",
    flag: "🇻🇳",
    code: "VN",
    dial_code: "+84",
    phone_placeholder: "123 456 7890",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["3", "5", "7", "8", "9"],
  },
  {
    name: "Virgin Islands, British",
    flag: "🇻🇬",
    code: "VG",
    dial_code: "+1284",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["284"],
  },
  {
    name: "Virgin Islands, U.S.",
    flag: "🇻🇮",
    code: "VI",
    dial_code: "+1340",
    phone_placeholder: "123 4567",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["340"],
  },
  {
    name: "Wallis and Futuna",
    flag: "🇼🇫",
    code: "WF",
    dial_code: "+681",
    phone_placeholder: "1234 5678",
    phone_pattern: "\\d\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["681"],
  },
  {
    name: "Yemen",
    flag: "🇾🇪",
    code: "YE",
    dial_code: "+967",
    phone_placeholder: "123 456 789",
    phone_pattern: "\\d\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
  {
    name: "Zambia",
    flag: "🇿🇲",
    code: "ZM",
    dial_code: "+260",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["9"],
  },
  {
    name: "Zimbabwe",
    flag: "🇿🇼",
    code: "ZW",
    dial_code: "+263",
    phone_placeholder: "12 345 6789",
    phone_pattern: "\\d\\d\\s?\\d\\d\\d\\s?\\d\\d\\d\\d",
    mobile_prefixes: ["7"],
  },
];

export default countries;



// WEBPACK FOOTER //
// ./src/app/shared/json/countries.ts